/**
 * The evaluation matrix — architecture §6 step 2 (EVALUATE).
 *
 * Pure, deterministic, no LLM, no wall clock (the window is anchored to the
 * packet `generatedAt` unless `opts.now` overrides it).
 *
 * Input : the `coach_gather_window` packet (types/coach.ts)
 * Output: EvaluationResult (types/results.ts)
 */
import type {
  ActivePlan,
  CoachInput,
  ExerciseTrendPoint,
  PlanExercise,
} from '../types/coach.js';
import type {
  EvaluateOptions,
  EvaluationMetrics,
  EvaluationResult,
  ExerciseInsight,
  FatigueFlag,
  TrendDirection,
  Verdict,
} from '../types/results.js';
import {
  acuteChronicRatio,
  epley1rm,
  linearSlope,
  mean,
  percentChange,
  round2,
} from './math.js';

// ---------------------------------------------------------------------------
// Named thresholds (single source of truth for the whole package + its tests)
// ---------------------------------------------------------------------------

export const THRESHOLDS = {
  /** < this many completed sessions in the window => insufficient_data. */
  MIN_COMPLETED_SESSIONS: 3,
  /** < this many distinct training weeks of trend data => insufficient_data. */
  MIN_WEEKS_OF_DATA: 2,
  /** A week whose average RPE is at or above this is a red flag. */
  HIGH_WEEK_AVG_RPE: 9.0,
  /** RPE climbing at least this fast per week over the last 2 weeks is creep. */
  RPE_SLOPE_PER_WEEK: 0.4,
  /** Failed sets above this share of working sets is a red flag. */
  MAX_FAILED_SET_RATE: 0.1,
  /** Week-over-week tonnage jump above this share is a spike. */
  MAX_WEEK_VOLUME_JUMP: 0.3,
  /** Acute:chronic workload ratio above this is overreaching. */
  ACWR_HIGH: 1.5,
  /** … and below this is under-training / lost fitness. */
  ACWR_LOW: 0.8,
  /** Compliance under this share is a collapse. */
  COMPLIANCE_COLLAPSE_PCT: 60,
  /** Compliance at or above this is needed before a stall is "real". */
  STALL_MIN_COMPLIANCE_PCT: 80,
  /** Weeks without an e1RM improvement before we call it a stall. */
  STALL_WEEKS: 3,
  /** Consecutive improving weeks that count as progressive overload. */
  PROGRESSIVE_WEEKS: 2,
  /** Exercises that must progress before the block is "progressing". */
  PROGRESSIVE_MIN_EXERCISES: 2,
  /** Window e1RM gain at or above this share is `progressing_fast`. */
  FAST_GAIN_PCT: 5,
  /** Fatigue flags needed to trigger a deload. */
  DELOAD_FLAG_COUNT: 3,
  /** Fatigue flags needed to call the block `overreaching` (below deload). */
  OVERREACH_FLAG_COUNT: 2,
  /** ACWR needed for a lone overreaching call. */
  OVERREACH_ACWR: 1.3,
  /** avg RPE + creep together, with no deload-level flag count, is overreaching. */
  OVERREACH_AVG_RPE: 9.2,
  /** Window e1RM change within +/- this share is "flat" for deload detection. */
  FLAT_E1RM_PCT: 1,
} as const;

/** Compound barbell-style lifts progress by 2.5 kg unless told otherwise. */
export const LOAD_INCREMENT_COMPOUND_KG = 2.5;
/** Accessories progress by one 1.25 kg plate pair per side. */
export const LOAD_INCREMENT_ACCESSORY_KG = 1.25;
/** Smallest plate jump the product understands. */
export const MIN_LOAD_STEP_KG = 0.5;
/** RPE a prescribed set has to stay under, relative to target, to earn a load increase. */
export const RPE_TOLERANCE = 0.5;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface WeekBucket {
  weekStart: string;
  volumeKg: number;
  workingSets: number;
  failedSets: number;
  rpeValues: number[];
  bestE1rmKg: number;
  sessions: number;
}

interface ExerciseSeries {
  exerciseId: string;
  exerciseName: string;
  points: ExerciseTrendPoint[];
  /** True when the movement is scored on top-set reps instead of e1RM. */
  bodyweight: boolean;
  /** Metric used for the trend (e1RM, or top-set weight × reps of the top set). */
  scores: number[];
  bestScore: number;
  changePct: number;
  trend: TrendDirection;
  /** Longest run of consecutive weeks whose score beat the previous week. */
  improvingRuns: number;
  /** Consecutive non-improving weeks at the end of the window. */
  stallWeeks: number;
  weeksAtCurrentLoad: number;
}

function isBodyweightMovement(name: string, topWeights: ReadonlyArray<number>): boolean {
  const lowered = name.toLowerCase();
  const bodyweightKeyword =
    lowered.includes('pull-up') ||
    lowered.includes('pull up') ||
    lowered.includes('chin-up') ||
    lowered.includes('chin up') ||
    lowered.includes('push-up') ||
    lowered.includes('push up') ||
    lowered.includes('dip') ||
    lowered.includes('muscle-up') ||
    lowered.includes('inverted row') ||
    lowered.includes('bodyweight');
  const neverLoaded = topWeights.every((weight) => !Number.isFinite(weight) || weight <= 0);
  return bodyweightKeyword && neverLoaded;
}

/** Working-set score for a bodyweight movement: top-set reps standing in for load. */
function bodyweightScore(point: ExerciseTrendPoint): number {
  const top = point.topWeightKg ?? 0;
  const e1rm = point.bestE1rmKg ?? 0;
  if (e1rm > 0 && top > 0) {
    // e1rm = top * (1 + reps/30) (reps capped at 12) => reps = 30 * (e1rm/top - 1)
    const inferred = 30 * (e1rm / top - 1);
    return round2(Math.min(Math.max(inferred, 1), 100));
  }
  // No load at all: the only progressive signal available is the set count itself.
  return point.workingSets;
}

function buildWeekBuckets(
  sessions: CoachInput['sessions'],
  trend: ReadonlyArray<ExerciseTrendPoint>,
): WeekBucket[] {
  const buckets = new Map<string, WeekBucket>();
  const bucketFor = (weekStart: string): WeekBucket => {
    const existing = buckets.get(weekStart);
    if (existing) return existing;
    const created: WeekBucket = {
      weekStart,
      volumeKg: 0,
      workingSets: 0,
      failedSets: 0,
      rpeValues: [],
      bestE1rmKg: 0,
      sessions: 0,
    };
    buckets.set(weekStart, created);
    return created;
  };

  // Volume + compliance come from sessions (the authoritative total per workout).
  const sortedSessions = [...sessions].sort((a, b) => a.startedAt.localeCompare(b.startedAt));
  for (const session of sortedSessions) {
    const bucket = bucketFor(isoWeekStart(session.startedAt));
    bucket.volumeKg += session.volumeKg;
    bucket.workingSets += session.setsCompleted;
    bucket.failedSets += session.failedSets;
    bucket.sessions += 1;
    if (session.avgRpe !== null && Number.isFinite(session.avgRpe)) {
      // One entry per session: the week's RPE is the mean of its session RPEs,
      // which is what a lifter means by "this week felt like a 9".
      bucket.rpeValues.push(session.avgRpe);
    }
  }

  // Exercise-level detail comes from the weekly progress view.
  for (const point of trend) {
    const bucket = bucketFor(point.weekStart);
    const e1rm = point.bestE1rmKg ?? 0;
    if (e1rm > bucket.bestE1rmKg) bucket.bestE1rmKg = e1rm;
    if (point.workingSets > 0 && bucket.sessions === 0) {
      // Trend-only packets (no sessions array) still get working-set counts.
      bucket.workingSets += point.workingSets;
      bucket.failedSets += point.failedSets;
    }
  }

  return [...buckets.values()].sort((a, b) => a.weekStart.localeCompare(b.weekStart));
}

/** ISO-8601 week start (Monday) for any timestamp, without a date library. */
export function isoWeekStart(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp.slice(0, 10);
  const utc = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = utc.getUTCDay(); // 0 = Sunday
  const delta = day === 0 ? -6 : 1 - day;
  utc.setUTCDate(utc.getUTCDate() + delta);
  return utc.toISOString().slice(0, 10);
}

function windowDaysBetween(fromIso: string, toIso: string): number {
  const from = new Date(fromIso).getTime();
  const to = new Date(toIso).getTime();
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 0;
  return Math.max(0, Math.round((to - from) / 86_400_000));
}

function buildExerciseSeries(trend: ReadonlyArray<ExerciseTrendPoint>): ExerciseSeries[] {
  const byExercise = new Map<string, ExerciseTrendPoint[]>();
  for (const point of trend) {
    const existing = byExercise.get(point.exerciseId);
    if (existing) existing.push(point);
    else byExercise.set(point.exerciseId, [point]);
  }

  const series: ExerciseSeries[] = [];
  for (const [exerciseId, rawPoints] of byExercise) {
    const points = [...rawPoints].sort((a, b) => a.weekStart.localeCompare(b.weekStart));
    const first = points[0];
    if (!first) continue;
    const bodyweight = isBodyweightMovement(
      first.exerciseName,
      points.map((point) => point.topWeightKg ?? 0),
    );
    const scores = points.map((point) =>
      bodyweight ? bodyweightScore(point) : (point.bestE1rmKg ?? 0),
    );
    const bestScore = scores.reduce((best, value) => (value > best ? value : best), 0);
    const firstScore = scores[0] ?? 0;
    const lastScore = scores[scores.length - 1] ?? 0;
    const changePct = percentChange(firstScore, lastScore);

    let run = 0;
    let longestRun = 0;
    let stallWeeks = 0;
    for (let index = 1; index < scores.length; index += 1) {
      const previous = scores[index - 1] ?? 0;
      const current = scores[index] ?? 0;
      if (current > previous) {
        run += 1;
        if (run > longestRun) longestRun = run;
        stallWeeks = 0;
      } else {
        run = 0;
        stallWeeks += 1;
      }
    }

    let weeksAtCurrentLoad = 0;
    const lastTopWeight = points[points.length - 1]?.topWeightKg ?? 0;
    for (let index = points.length - 1; index >= 0; index -= 1) {
      const point = points[index];
      if (!point) break;
      if (round2(point.topWeightKg ?? 0) === round2(lastTopWeight)) weeksAtCurrentLoad += 1;
      else break;
    }

    const trendDirection: TrendDirection =
      scores.length < 2
        ? 'insufficient_data'
        : changePct > THRESHOLDS.FLAT_E1RM_PCT
          ? 'improving'
          : changePct < -THRESHOLDS.FLAT_E1RM_PCT
            ? 'declining'
            : 'flat';

    series.push({
      exerciseId,
      exerciseName: first.exerciseName,
      points,
      bodyweight,
      scores,
      bestScore,
      changePct,
      trend: trendDirection,
      improvingRuns: longestRun,
      stallWeeks,
      weeksAtCurrentLoad,
    });
  }

  return series;
}

function improvingRunsOf(exercise: ExerciseSeries): number {
  return exercise.improvingRuns;
}

function recommendationFor(exercise: ExerciseSeries, compliancePct: number): {
  recommendation: string;
  reason: string;
} {
  if (exercise.trend === 'insufficient_data') {
    return {
      recommendation: 'hold',
      reason: `Only one week of ${exercise.exerciseName} data in the window — hold the load and log another week.`,
    };
  }
  if (exercise.trend === 'declining') {
    return {
      recommendation: 'reduce_load',
      reason: `${exercise.exerciseName} is down ${Math.abs(exercise.changePct).toFixed(1)}% on e1RM across the window. Take 5% off and rebuild the rep quality.`,
    };
  }
  if (
    exercise.stallWeeks >= THRESHOLDS.STALL_WEEKS &&
    compliancePct >= THRESHOLDS.STALL_MIN_COMPLIANCE_PCT
  ) {
    return {
      recommendation: 'change_rep_scheme',
      reason: `${exercise.exerciseName} has not moved for ${exercise.stallWeeks} weeks at ${compliancePct.toFixed(1)}% compliance. Change the rep scheme or drop the load 5% to reset.`,
    };
  }
  if (exercise.trend === 'improving' && improvingRunsOf(exercise) >= THRESHOLDS.PROGRESSIVE_WEEKS) {
    return {
      recommendation: 'increase_load',
      reason: `${exercise.exerciseName} is up ${exercise.changePct.toFixed(1)}% over the window with ${improvingRunsOf(exercise)} consecutive improving weeks. Earned overload — add load.`,
    };
  }
  return {
    recommendation: 'hold_and_add_rep',
    reason: `${exercise.exerciseName} is holding at ${exercise.bestScore.toFixed(2)} kg ${exercise.bodyweight ? 'top-set reps' : 'e1RM'}. Hold the load and take the top set one rep further.`,
  };
}

function buildHeadline(
  verdict: Verdict,
  metrics: EvaluationMetrics,
  flags: ReadonlyArray<FatigueFlag>,
  insights: ReadonlyArray<ExerciseInsight>,
): string {
  const improving = insights.filter((insight) => insight.trend === 'improving');
  const topMover = [...insights].sort((a, b) => b.e1rmChangePct - a.e1rmChangePct)[0];
  const prText =
    metrics.prCount > 0
      ? ` ${metrics.prCount} PR${metrics.prCount === 1 ? '' : 's'} logged.`
      : '';

  let base: string;
  switch (verdict) {
    case 'insufficient_data':
      base = `Only ${metrics.sessionsAnalyzed} completed session${metrics.sessionsAnalyzed === 1 ? '' : 's'} in this window. Log one more week and the coach can call it.`;
      break;
    case 'deload_recommended':
      base = `${flags.length} fatigue flags on ${metrics.sessionsAnalyzed} sessions at ${metrics.avgRpe.toFixed(2)} avg RPE — deload next week, then go again.`;
      break;
    case 'overreaching':
      base = `RPE is up (${metrics.avgRpe.toFixed(2)} avg) and the trend is flattening — pull volume back 20% before it costs you a week.`;
      break;
    case 'progressing_fast':
      base = `${topMover ? `${topMover.exerciseName} up ${topMover.e1rmChangePct.toFixed(1)}%` : 'The block is ahead of plan'} — hold the plan and keep the reps clean.`;
      break;
    case 'progressing':
      base = `${improving.length} lift${improving.length === 1 ? '' : 's'} improved across ${metrics.weeksAnalyzed} weeks — that is earned overload.`;
      break;
    case 'stalled':
      base = `Nothing moved this block at ${metrics.compliancePct.toFixed(1)}% compliance — change the rep scheme instead of adding weight.`;
      break;
    case 'on_track':
    default:
      base = `Holding steady at ${metrics.compliancePct.toFixed(1)}% compliance and ${metrics.avgRpe.toFixed(2)} avg RPE.`;
      break;
  }
  return `${base}${prText}`;
}

// ---------------------------------------------------------------------------
// The matrix
// ---------------------------------------------------------------------------

/**
 * Evaluate a gathered block. Deterministic: identical packets produce identical
 * results, and `opts.now` (never `Date.now()`) anchors every time-relative rule.
 */
export function evaluateBlock(packet: CoachInput, opts?: EvaluateOptions): EvaluationResult {
  const now = opts?.now ?? packet.generatedAt;
  const minSessions = opts?.minSessions ?? THRESHOLDS.MIN_COMPLETED_SESSIONS;
  const minWeeks = opts?.minWeeks ?? THRESHOLDS.MIN_WEEKS_OF_DATA;

  const sessions = [...packet.sessions].sort((a, b) => a.startedAt.localeCompare(b.startedAt));
  const weeks = buildWeekBuckets(sessions, packet.exerciseTrend);
  const series = buildExerciseSeries(packet.exerciseTrend);
  const dataQuality: string[] = [];

  const weeklyVolumes = weeks.map((week) => week.volumeKg);
  // Unrounded weekly session-RPE means; the slope is computed on these so that
  // display rounding (2dp) cannot hide or invent a fatigue trend.
  const weeklyRpeRaw = weeks.map(
    (week) =>
      week.rpeValues.reduce((sum, value) => sum + value, 0) / Math.max(week.rpeValues.length, 1),
  );
  const weeklyRpe = weeklyRpeRaw.map((value) => round2(value));
  const acwr = acuteChronicRatio(weeklyVolumes, 1);

  const totalVolumeKg = round2(weeklyVolumes.reduce((sum, value) => sum + value, 0));
  const firstWeekVolume = weeklyVolumes[0] ?? 0;
  const lastWeekVolume = weeklyVolumes[weeklyVolumes.length - 1] ?? 0;
  const volumeChangePct = percentChange(firstWeekVolume, lastWeekVolume);
  // Two independent sources: `v_session_totals` (sessions) and
  // `v_weekly_exercise_progress` (exerciseTrend). They can disagree — a failed set
  // may be logged but never promoted to a completed per-exercise row — so the
  // matrix takes the higher rate, which can only make the fatigue signal louder.
  const sessionWorkingSets = weeks.reduce((sum, week) => sum + week.workingSets, 0);
  const sessionFailedSets = weeks.reduce((sum, week) => sum + week.failedSets, 0);
  const trendWorkingSets = packet.exerciseTrend.reduce((sum, point) => sum + point.workingSets, 0);
  const trendFailedSets = packet.exerciseTrend.reduce((sum, point) => sum + point.failedSets, 0);
  const sessionFailedRate = sessionWorkingSets > 0 ? (sessionFailedSets / sessionWorkingSets) * 100 : 0;
  const trendFailedRate = trendWorkingSets > 0 ? (trendFailedSets / trendWorkingSets) * 100 : 0;
  // Rates are compared per source (a ratio, not a raw count) so that the louder
  // denominator cannot dilute a genuine cluster of misses.
  const failedSetRate = round2(Math.max(sessionFailedRate, trendFailedRate));
  const failedSetSample =
    trendFailedRate > sessionFailedRate
      ? { source: 'exerciseTrend' as const, failedSets: trendFailedSets, workingSets: trendWorkingSets }
      : { source: 'sessions' as const, failedSets: sessionFailedSets, workingSets: sessionWorkingSets };
  const avgRpe = mean(weeklyRpe.filter((value) => value > 0));
  const compliancePct =
    packet.compliance.avgCompliancePct !== null && packet.compliance.avgCompliancePct !== undefined
      ? round2(packet.compliance.avgCompliancePct)
      : weeks.length > 0
        ? round2(
            (sessions.length /
              Math.max(weeks.length * (packet.profile.weeklyTarget ?? 3), 1)) *
              100,
          )
        : 0;
  const prCount = packet.personalRecords.length;

  const metrics: EvaluationMetrics = {
    totalVolumeKg,
    volumeChangePct,
    avgRpe,
    compliancePct,
    sessionsAnalyzed: sessions.length,
    prCount,
    acuteChronicRatio: acwr,
    weeksAnalyzed: weeks.length,
  };

  // --- fatigue flags -------------------------------------------------------
  const fatigueFlags: FatigueFlag[] = [];
  const flag = (entry: FatigueFlag): void => {
    fatigueFlags.push(entry);
  };

  const lastWeek = weeks[weeks.length - 1];

  const lastWeekRpeRaw = lastWeek
    ? lastWeek.rpeValues.reduce((sum, value) => sum + value, 0) / Math.max(lastWeek.rpeValues.length, 1)
    : 0;
  const lastWeekRpe = round2(lastWeekRpeRaw);
  if (lastWeek && lastWeekRpe >= THRESHOLDS.HIGH_WEEK_AVG_RPE) {
    flag({
      code: 'weekly_avg_rpe_high',
      severity: 'critical',
      message: `Week of ${lastWeek.weekStart} averaged RPE ${lastWeekRpe.toFixed(2)}. That is maximal-effort territory.`,
      evidence: {
        weekStart: lastWeek.weekStart,
        weekAvgRpe: lastWeekRpe,
        threshold: THRESHOLDS.HIGH_WEEK_AVG_RPE,
      },
    });
  }

  if (weeklyRpe.length >= 2) {
    const recent = weeklyRpeRaw.slice(-2);
    const windowSlope = linearSlope(weeklyRpeRaw);
    const recentSlope = linearSlope(recent);
    // `RPE_SLOPE_PER_WEEK` is expressed per week. We accept creep when either the
    // last two weeks are climbing that fast, or the whole window is — a two-point
    // fit is noisy, the window fit is the stronger signal, and the rule is "the
    // trend is up", so using both is strictly more sensitive and never less.
    const slope = Math.max(windowSlope, recentSlope);
    if (slope >= THRESHOLDS.RPE_SLOPE_PER_WEEK) {
      flag({
        code: 'rpe_creep',
        severity: 'warning',
        message: `RPE is climbing ${slope.toFixed(2)} per week (${recent.map((value) => value.toFixed(2)).join(' → ')}). Same loads, more effort.`,
        evidence: {
          slopePerWeek: slope,
          windowSlopePerWeek: windowSlope,
          recentSlopePerWeek: recentSlope,
          threshold: THRESHOLDS.RPE_SLOPE_PER_WEEK,
          window: recent.join(','),
          weekAvgRpe: weeklyRpe.join(','),
        },
      });
    }
  }

  if (failedSetRate > THRESHOLDS.MAX_FAILED_SET_RATE * 100) {
    flag({
      code: 'failed_set_rate_high',
      severity: 'critical',
      message: `${failedSetSample.failedSets} failed sets (${failedSetRate.toFixed(1)}% of working sets). Grinders are fine; misses are data.`,
      evidence: {
        failedSets: failedSetSample.failedSets,
        workingSets: failedSetSample.workingSets,
        failedSetRatePct: failedSetRate,
        source: failedSetSample.source,
        sessionFailedSetRatePct: round2(sessionFailedRate),
        trendFailedSetRatePct: round2(trendFailedRate),
        thresholdPct: round2(THRESHOLDS.MAX_FAILED_SET_RATE * 100),
      },
    });
  }

  for (let index = 1; index < weeks.length; index += 1) {
    const previous = weeks[index - 1];
    const current = weeks[index];
    if (!previous || !current || previous.volumeKg <= 0) continue;
    const jump = percentChange(previous.volumeKg, current.volumeKg);
    if (jump > THRESHOLDS.MAX_WEEK_VOLUME_JUMP * 100) {
      flag({
        code: 'volume_spike',
        severity: 'warning',
        message: `Volume jumped ${jump.toFixed(1)}% into the week of ${current.weekStart} (${previous.volumeKg.toFixed(0)} → ${current.volumeKg.toFixed(0)} kg).`,
        evidence: {
          fromWeek: previous.weekStart,
          toWeek: current.weekStart,
          changePct: jump,
          thresholdPct: round2(THRESHOLDS.MAX_WEEK_VOLUME_JUMP * 100),
        },
      });
      break;
    }
  }

  if (acwr > 0 && acwr > THRESHOLDS.ACWR_HIGH) {
    flag({
      code: 'acute_chronic_ratio_high',
      severity: 'warning',
      message: `Acute:chronic workload is ${acwr.toFixed(2)} — this week is far above your 4-week baseline.`,
      evidence: { ratio: acwr, threshold: THRESHOLDS.ACWR_HIGH },
    });
  }
  if (acwr > 0 && acwr < THRESHOLDS.ACWR_LOW) {
    flag({
      code: 'acute_chronic_ratio_low',
      severity: 'warning',
      message: `Acute:chronic workload is ${acwr.toFixed(2)} — this week is well under your baseline.`,
      evidence: { ratio: acwr, threshold: THRESHOLDS.ACWR_LOW },
    });
  }

  if (compliancePct < THRESHOLDS.COMPLIANCE_COLLAPSE_PCT) {
    flag({
      code: 'compliance_collapse',
      severity: 'critical',
      message: `Compliance is ${compliancePct.toFixed(1)}% — sessions are being missed, so load is not the problem yet.`,
      evidence: { compliancePct, thresholdPct: THRESHOLDS.COMPLIANCE_COLLAPSE_PCT },
    });
  }

  // --- per-exercise insights ----------------------------------------------
  const exerciseInsights: ExerciseInsight[] = series.map((exercise) => {
    const { recommendation, reason } = recommendationFor(exercise, compliancePct);
    return {
      exerciseId: exercise.exerciseId,
      exerciseName: exercise.exerciseName,
      trend: exercise.trend,
      bestE1rmKg: round2(exercise.bestScore),
      e1rmChangePct: exercise.changePct,
      weeksAtCurrentLoad: exercise.weeksAtCurrentLoad,
      recommendation,
      reason,
      stallWeeks: exercise.stallWeeks,
      bodyweight: exercise.bodyweight,
      weeklyPoints: exercise.points.map((point) => ({
        weekStart: point.weekStart,
        bestE1rmKg: round2(point.bestE1rmKg ?? 0),
        topWeightKg: round2(point.topWeightKg ?? 0),
        workingSets: point.workingSets,
      })),
    };
  });

  // --- verdict precedence --------------------------------------------------
  const progressiveExercises = series.filter(
    (exercise) => improvingRunsOf(exercise) >= THRESHOLDS.PROGRESSIVE_WEEKS,
  );
  const headlineGain = series.reduce(
    (best, exercise) => (exercise.changePct > best ? exercise.changePct : best),
    Number.NEGATIVE_INFINITY,
  );
  const progressing = progressiveExercises.length >= THRESHOLDS.PROGRESSIVE_MIN_EXERCISES;
  const progressingFast = progressing && headlineGain >= THRESHOLDS.FAST_GAIN_PCT;
  const anyImproving = series.some((exercise) => exercise.trend === 'improving');
  const stalledExercises = series.filter(
    (exercise) =>
      exercise.stallWeeks >= THRESHOLDS.STALL_WEEKS &&
      compliancePct >= THRESHOLDS.STALL_MIN_COMPLIANCE_PCT,
  );

  const rpeTrendingUp = linearSlope(weeklyRpeRaw) >= THRESHOLDS.RPE_SLOPE_PER_WEEK;
  const e1rmFlat = !series.some(
    (exercise) => Math.abs(exercise.changePct) > THRESHOLDS.FLAT_E1RM_PCT && exercise.changePct > 0,
  );
  const deload =
    fatigueFlags.length >= THRESHOLDS.DELOAD_FLAG_COUNT ||
    (rpeTrendingUp && e1rmFlat);
  const overreaching =
    !deload &&
    (fatigueFlags.length >= THRESHOLDS.OVERREACH_FLAG_COUNT ||
      acwr >= THRESHOLDS.OVERREACH_ACWR ||
      (avgRpe >= THRESHOLDS.OVERREACH_AVG_RPE &&
        fatigueFlags.some((entry) => entry.code === 'rpe_creep')));

  const enoughData =
    sessions.length >= minSessions && weeks.length >= minWeeks && series.length > 0;

  const verdictReasons: string[] = [];
  let verdict: Verdict;
  if (!enoughData) {
    verdict = 'insufficient_data';
    verdictReasons.push(
      `${sessions.length} completed session(s) over ${weeks.length} week(s); the matrix needs >= ${minSessions} sessions and >= ${minWeeks} weeks.`,
    );
  } else if (overreaching) {
    verdict = 'overreaching';
    verdictReasons.push(
      `${fatigueFlags.length} fatigue flag(s) (ACWR ${acwr.toFixed(2)}, avg RPE ${avgRpe.toFixed(2)}) but below the 3-flag deload trigger.`,
    );
  } else if (deload) {
    verdict = 'deload_recommended';
    verdictReasons.push(
      fatigueFlags.length >= THRESHOLDS.DELOAD_FLAG_COUNT
        ? `${fatigueFlags.length} fatigue flags met the deload trigger (>= ${THRESHOLDS.DELOAD_FLAG_COUNT}).`
        : 'RPE is trending up while e1RM is flat — deload before the plateau costs a block.',
    );
  } else if (progressingFast) {
    verdict = 'progressing_fast';
    verdictReasons.push(
      `${progressiveExercises.length} exercises improved in consecutive weeks and the best window gain is ${headlineGain.toFixed(1)}% (>= ${THRESHOLDS.FAST_GAIN_PCT}%).`,
    );
  } else if (progressing) {
    verdict = 'progressing';
    verdictReasons.push(
      `${progressiveExercises.length} exercises improved for >= ${THRESHOLDS.PROGRESSIVE_WEEKS} consecutive weeks.`,
    );
  } else if (!anyImproving && stalledExercises.length > 0) {
    verdict = 'stalled';
    verdictReasons.push(
      `${stalledExercises.length} exercise(s) flat for >= ${THRESHOLDS.STALL_WEEKS} weeks at ${compliancePct.toFixed(1)}% compliance and no fatigue flags.`,
    );
  } else {
    verdict = 'on_track';
    verdictReasons.push(
      `No stall and no fatigue flags: holding at ${compliancePct.toFixed(1)}% compliance and ${avgRpe.toFixed(2)} avg RPE.`,
    );
  }

  // --- data-quality notes -------------------------------------------------
  if (packet.compliance.avgCompliancePct === null || packet.compliance.avgCompliancePct === undefined) {
    dataQuality.push('No plan-adherence rows in the window; compliance estimated from sessions and weekly target.');
  }
  if (series.length === 0) {
    dataQuality.push('No weekly exercise trend rows in the window; per-exercise insight is unavailable.');
  }
  if (sessions.some((session) => session.avgRpe === null)) {
    dataQuality.push('At least one session has no session RPE; fatigue detection is weaker for those days.');
  }
  if (packet.exerciseTrend.some((point) => point.workingSets === 0)) {
    dataQuality.push('Some weekly trend rows contain zero working sets (for example an unloaded bodyweight movement).');
  }
  if (!packet.activePlan) {
    dataQuality.push('No active plan: the coach can evaluate but cannot prescribe plan changes.');
  }
  const windowDays = windowDaysBetween(packet.windowFrom, now);
  if (windowDays > 0 && windowDays !== packet.windowDays) {
    dataQuality.push(
      `Packet declares a ${packet.windowDays}-day window but spans ${windowDays} days from windowFrom to now.`,
    );
  }
  if (packet.exerciseTrend.some((point) => !point.sets || point.sets.length === 0)) {
    dataQuality.push('The packet carries week-level aggregates only; set-level reps are inferred from e1RM and top weight.');
  }

  const headline = buildHeadline(verdict, metrics, fatigueFlags, exerciseInsights);

  return {
    verdict,
    headline,
    metrics,
    exerciseInsights,
    fatigueFlags,
    dataQuality,
    verdictReasons,
  };
}

/** All plan exercises, flattened, in plan order — used by the progression engine. */
export function flattenPlanExercises(plan: ActivePlan | null): Array<{
  dayId: string;
  dayLabel: string;
  dayIndex: number;
  exercise: PlanExercise;
}> {
  if (!plan) return [];
  const flat: Array<{ dayId: string; dayLabel: string; dayIndex: number; exercise: PlanExercise }> = [];
  const days = [...plan.days].sort((a, b) => a.dayIndex - b.dayIndex);
  for (const day of days) {
    const exercises = [...day.exercises].sort((a, b) => a.orderIndex - b.orderIndex);
    for (const exercise of exercises) {
      flat.push({ dayId: day.dayId, dayLabel: day.label, dayIndex: day.dayIndex, exercise });
    }
  }
  return flat;
}

/** Best-effort weekly total volume for a plan (sets × load), used by the safety rails. */
export function planWeeklyVolume(plan: ActivePlan | null): number {
  const flat = flattenPlanExercises(plan);
  let total = 0;
  for (const { exercise } of flat) {
    total += Math.max(exercise.sets, 0) * Math.max(exercise.targetWeightKg ?? 0, 0);
  }
  return round2(total);
}

/** Epley re-export convenience for callers that only import the matrix. */
export { epley1rm };
